<template>
  <Transition name="discount-toast">
    <div v-if="visible" class="discount-toast" role="dialog" aria-label="Предложение скидки">
      <div class="discount-toast__inner">
        <p class="discount-toast__text">Получить скидку на заказ</p>
        <div class="discount-toast__actions">
          <button type="button" class="discount-toast__btn" @click="goToFortune">
            Получить скидку
          </button>
          <button type="button" class="discount-toast__never" @click="neverShow">
            Больше не показывать
          </button>
          <button type="button" class="discount-toast__close" aria-label="Закрыть" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const STORAGE_KEY_CLOSED = 'discount_toast_closed'
const STORAGE_KEY_FORT = 'fort'
const SCROLL_THRESHOLD = 120

export default {
  name: 'DiscountToast',
  props: {
    splashDone: { type: Boolean, default: false },
  },
  setup(props) {
    const router = useRouter()
    const route = useRoute()
    const visible = ref(false)
    let showTimeout = null
    let scrollListener = null
    let didTryShowAfterScroll = false

    function close() {
      visible.value = false
    }

    function goToFortune() {
      visible.value = false
      router.push('/fortune')
    }

    function neverShow() {
      try {
        localStorage.setItem(STORAGE_KEY_CLOSED, '1')
      } catch (e) {}
      visible.value = false
    }

    function tryShow() {
      if (didTryShowAfterScroll) return
      if (route.path === '/fortune') return
      try {
        if (localStorage.getItem(STORAGE_KEY_CLOSED)) return
        if (localStorage.getItem(STORAGE_KEY_FORT)) return
      } catch (e) {}
      didTryShowAfterScroll = true
      showTimeout = setTimeout(() => {
        visible.value = true
      }, 400)
    }

    function onScroll() {
      if (window.scrollY >= SCROLL_THRESHOLD) {
        if (scrollListener) {
          window.removeEventListener('scroll', scrollListener, { passive: true })
          scrollListener = null
        }
        tryShow()
      }
    }

    watch(
      () => props.splashDone,
      (done) => {
        if (!done) return
        scrollListener = onScroll
        if (window.scrollY >= SCROLL_THRESHOLD) {
          tryShow()
        } else {
          window.addEventListener('scroll', scrollListener, { passive: true })
        }
      },
      { immediate: true }
    )

    watch(
      () => route.path,
      (path) => {
        if (path === '/fortune') visible.value = false
      }
    )

    onBeforeUnmount(() => {
      if (showTimeout) clearTimeout(showTimeout)
      if (scrollListener) window.removeEventListener('scroll', scrollListener, { passive: true })
    })

    return { visible, close, goToFortune, neverShow }
  },
}
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";

.discount-toast {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5000;
  padding: 12px $gap 16px;
  pointer-events: none;
}

.discount-toast__inner {
  pointer-events: auto;
  max-width: $container-max;
  margin: 0 auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.9);
  color: $color-primary-inverse;
  border-radius: 999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);

  @media (max-width: $breakpoint-mobile) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    border-radius: 16px;
  }
}

.discount-toast__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: $breakpoint-mobile) {
    width: 100%;
    justify-content: flex-start;
  }
}

.discount-toast__text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.9;
  flex: 1;
  min-width: 0;
}

.discount-toast__btn {
  flex-shrink: 0;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: $color-accent-teal;
  color: $color-primary-inverse;
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    background: darken($color-accent-teal, 6%);
    transform: translateY(-1px);
  }
}

.discount-toast__never {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  line-height: 1.5;
  color: $color-primary-inverse;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  opacity: 0.9;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

.discount-toast__close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: $color-primary-inverse;
  border-radius: 8px;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s;
}

.discount-toast__close:hover {
  opacity: 1;
}

.discount-toast-enter-active,
.discount-toast-leave-active {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;
}
.discount-toast-enter-from {
  transform: translateY(100%);
  opacity: 0;
}
.discount-toast-enter-to {
  transform: translateY(0);
  opacity: 1;
}
.discount-toast-leave-to {
  transform: translateY(80%);
  opacity: 0;
}
</style>
